import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import {
  Loader2, ChevronDown, ChevronUp, Check, AlertTriangle, XCircle, PauseCircle,
} from 'lucide-react'
import { formatSpend } from '@/store/creditsStore'

/**
 * Shared pieces of the simplified Ask-AI dock (all three docks — orchestrator,
 * Claude, Codex/OpenCode — render through these).
 *
 * The dock used to interleave every progress line, per-turn token/cost line and
 * the raw agent output with the conversation. Now a chat is read as TURNS: the
 * user's message and the agent's replies stay at full weight, and everything the
 * agent did along the way folds into ONE card while it works (`WorkingCard`) and
 * ONE quiet line once it is done (`TurnFooter`), each with a "Show steps"
 * disclosure. Design: docs/plans/askai-dock-simplification.md.
 */

/** One thing the agent did during a turn, in plain words. */
export interface DockStep {
  text: string
  level?: 'info' | 'success' | 'warning' | 'error'
  elapsedS?: number | null
}

/** What a finished turn cost — shown only behind "Details". */
export interface TurnUsage {
  elapsedS?: number | null
  tokens?: number | null
  inputTokens?: number | null
  outputTokens?: number | null
  costUsd?: number | null
  calls?: number | null
}

// Compact token count: 940 → "940", 1234 → "1.2k", 1500000 → "1.5M".
export function fmtTokens(n?: number | null): string {
  const v = Math.max(0, Math.round(n || 0))
  if (v < 1000) return String(v)
  if (v < 1_000_000) return (v / 1000).toFixed(v < 10_000 ? 1 : 0) + 'k'
  return (v / 1_000_000).toFixed(1) + 'M'
}

// Human duration from seconds: 8 → "8s", 75 → "1m 15s", 600 → "10m".
export function fmtDuration(sec?: number | null): string {
  const s = Math.max(0, Math.round(sec || 0))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  const r = s % 60
  return r ? `${m}m ${String(r).padStart(2, '0')}s` : `${m}m`
}

/** Seconds since `start`, ticking once a second while `active`. */
export function useElapsed(start: Date | number | null | undefined, active: boolean): number | undefined {
  const startMs = start == null ? null : (typeof start === 'number' ? start : start.getTime())
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active || startMs == null) return
    setNow(Date.now())
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [active, startMs])
  if (startMs == null) return undefined
  return Math.max(0, Math.round((now - startMs) / 1000))
}

/**
 * Split a time-ordered list into turns: a new turn starts at every item for
 * which `startsTurn` is true. Items before the first such item form a leading
 * turn of their own (e.g. a resumed session's progress lines).
 */
export function groupTurns<T>(items: T[], startsTurn: (item: T) => boolean): T[][] {
  const turns: T[][] = []
  for (const item of items) {
    if (startsTurn(item) || turns.length === 0) turns.push([item])
    else turns[turns.length - 1].push(item)
  }
  return turns
}

/** Collapse consecutive duplicate step texts (tool chatter repeats a lot). */
export function dedupeSteps(steps: DockStep[]): DockStep[] {
  const out: DockStep[] = []
  for (const s of steps) {
    const text = (s.text || '').trim()
    if (!text) continue
    const prev = out[out.length - 1]
    if (prev && prev.text === text) {
      if (s.elapsedS != null) prev.elapsedS = s.elapsedS
      continue
    }
    out.push({ ...s, text })
  }
  return out
}

// ── Small styled primitives ────────────────────────────────────────────────

const linkBtn: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '0.25rem 0',
  background: 'none',
  border: 'none',
  color: 'var(--color-accent)',
  fontSize: '1rem',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const quietLinkBtn: CSSProperties = {
  ...linkBtn,
  color: 'var(--color-text-secondary)',
  fontSize: '0.9375rem',
  fontWeight: 400,
  textDecoration: 'underline',
  textUnderlineOffset: 2,
}

export const dockPrimaryBtn: CSSProperties = {
  height: 38,
  padding: '0 1rem',
  fontSize: '1rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
}

export const dockSecondaryBtn: CSSProperties = {
  height: 38,
  padding: '0 0.875rem',
  fontSize: '1rem',
  fontWeight: 500,
  border: '1px solid var(--color-border)',
  whiteSpace: 'nowrap',
}

function Spinner({ size = 16 }: { size?: number }) {
  return (
    <Loader2
      size={size}
      aria-hidden="true"
      style={{ animation: 'spin 1s linear infinite', color: 'var(--color-accent)', flexShrink: 0 }}
    />
  )
}

function StepIcon({ level }: { level?: DockStep['level'] }) {
  const common = { size: 15, 'aria-hidden': true as const, style: { flexShrink: 0, marginTop: 3 } }
  if (level === 'warning') return <AlertTriangle {...common} style={{ ...common.style, color: 'var(--color-warning)' }} />
  if (level === 'error') return <XCircle {...common} style={{ ...common.style, color: 'var(--color-error)' }} />
  return <Check {...common} style={{ ...common.style, color: 'var(--color-success)' }} />
}

/** The plain-English list of what the agent did. `activeText` is the step in
 *  progress right now (drawn last, with a spinner). */
export function StepList({ steps, activeText }: { steps: DockStep[]; activeText?: string }) {
  return (
    <ol
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.4rem',
        maxHeight: 320,
        overflowY: 'auto',
      }}
    >
      {steps.map((s, i) => (
        <li
          key={i}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.5rem',
            fontSize: '1rem',
            lineHeight: 1.5,
            color: s.level === 'error' ? 'var(--color-error)' : 'var(--color-text)',
          }}
        >
          <StepIcon level={s.level} />
          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{s.text}</span>
          {s.elapsedS != null && s.elapsedS > 0 && (
            <span style={{ flexShrink: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtDuration(s.elapsedS)}
            </span>
          )}
        </li>
      ))}
      {activeText && (
        <li style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '1rem', lineHeight: 1.5, fontWeight: 500 }}>
          <span style={{ marginTop: 3, display: 'inline-flex' }}><Spinner size={15} /></span>
          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{activeText}</span>
        </li>
      )}
    </ol>
  )
}

function Disclosure({
  open, onToggle, openLabel, closedLabel, quiet = false,
}: {
  open: boolean
  onToggle: () => void
  openLabel: string
  closedLabel: string
  quiet?: boolean
}) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} style={quiet ? quietLinkBtn : linkBtn}>
      {open ? openLabel : closedLabel}
      {!quiet && (open ? <ChevronUp size={15} aria-hidden="true" /> : <ChevronDown size={15} aria-hidden="true" />)}
    </button>
  )
}

/**
 * The ONE card shown while a turn is running: what the agent is doing now, how
 * long it has been at it, and — behind a disclosure — the steps so far and the
 * raw live output (if the backend streams any).
 */
export function WorkingCard({
  label, detail, elapsedS, steps, liveOutput,
}: {
  label: string
  detail?: string | null
  elapsedS?: number | null
  steps: DockStep[]
  liveOutput?: string | null
}) {
  const [stepsOpen, setStepsOpen] = useState(false)
  const [outputOpen, setOutputOpen] = useState(false)
  return (
    <section
      aria-label="What the agent is doing"
      style={{
        border: '1px solid color-mix(in srgb, var(--color-accent) 38%, transparent)',
        background: 'var(--color-accent-weak)',
        borderRadius: 'var(--radius-lg)',
        padding: '0.875rem 1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.375rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
        <Spinner size={18} />
        <span
          aria-live="polite"
          style={{ flex: 1, minWidth: 0, fontWeight: 600, fontSize: '1.0625rem', lineHeight: 1.4, overflowWrap: 'anywhere' }}
        >
          {label}
        </span>
        {elapsedS != null && elapsedS > 0 && (
          <span
            title="Time spent on this request"
            style={{ flexShrink: 0, fontSize: '0.9375rem', color: 'var(--color-text-secondary)', fontVariantNumeric: 'tabular-nums' }}
          >
            {fmtDuration(elapsedS)}
          </span>
        )}
      </div>
      {detail && detail !== label && (
        <div style={{ fontSize: '1rem', color: 'var(--color-text-secondary)', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
          {detail}
        </div>
      )}
      {(steps.length > 0 || !!liveOutput) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem 1rem' }}>
          {steps.length > 0 && (
            <Disclosure
              open={stepsOpen}
              onToggle={() => setStepsOpen((v) => !v)}
              openLabel="Hide steps"
              closedLabel={`Show steps (${steps.length})`}
            />
          )}
          {!!liveOutput && (
            <Disclosure
              open={outputOpen}
              onToggle={() => setOutputOpen((v) => !v)}
              openLabel="Hide live output"
              closedLabel="Show live output"
            />
          )}
        </div>
      )}
      {stepsOpen && steps.length > 0 && (
        <div style={{ borderTop: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)', paddingTop: '0.625rem' }}>
          <StepList steps={steps} />
        </div>
      )}
      {outputOpen && !!liveOutput && (
        <pre
          style={{
            margin: 0,
            padding: '0.625rem 0.75rem',
            background: 'var(--color-input-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            fontFamily: 'var(--font-mono)',
            fontSize: '0.875rem',
            lineHeight: 1.6,
            color: 'var(--color-text)',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {liveOutput}
        </pre>
      )}
    </section>
  )
}

/**
 * The quiet line under a finished turn — "Took 3m 10s · 4 files changed ·
 * Details · Show steps (14)". Cost, tokens and the file list live behind
 * Details; the step list behind Show steps. Renders nothing when there is
 * nothing to say.
 */
export function TurnFooter({
  usage, steps, files = [],
}: {
  usage?: TurnUsage | null
  steps: DockStep[]
  files?: string[]
}) {
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [stepsOpen, setStepsOpen] = useState(false)
  const spend = formatSpend(usage?.costUsd)
  const totalTokens = usage?.tokens ?? ((usage?.inputTokens || 0) + (usage?.outputTokens || 0))
  const hasDetails = !!spend || totalTokens > 0 || !!usage?.calls || files.length > 0
  const took = usage?.elapsedS != null && usage.elapsedS > 0 ? `Took ${fmtDuration(usage.elapsedS)}` : null
  if (!took && !hasDetails && steps.length === 0) return null

  const sep = <span aria-hidden="true">·</span>
  const parts: ReactNode[] = []
  if (took) parts.push(<span key="took">{took}</span>)
  if (files.length > 0) {
    parts.push(<span key="files">{files.length} file{files.length === 1 ? '' : 's'} changed</span>)
  }
  if (hasDetails) {
    parts.push(
      <Disclosure key="details" quiet open={detailsOpen} onToggle={() => setDetailsOpen((v) => !v)}
        openLabel="Hide details" closedLabel="Details" />,
    )
  }
  if (steps.length > 0) {
    parts.push(
      <Disclosure key="steps" quiet open={stepsOpen} onToggle={() => setStepsOpen((v) => !v)}
        openLabel="Hide steps" closedLabel={`Show steps (${steps.length})`} />,
    )
  }

  const row = (label: string, value: ReactNode) => (
    <>
      <dt style={{ color: 'var(--color-text-secondary)' }}>{label}</dt>
      <dd style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>{value}</dd>
    </>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.25rem 0.5rem',
          fontSize: '0.9375rem', color: 'var(--color-text-secondary)',
        }}
      >
        {parts.map((p, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            {i > 0 && sep}
            {p}
          </span>
        ))}
      </div>
      {detailsOpen && hasDetails && (
        <dl
          style={{
            margin: 0,
            padding: '0.75rem 0.875rem',
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface-2)',
            borderRadius: 'var(--radius-md)',
            display: 'grid',
            gridTemplateColumns: 'max-content minmax(0, 1fr)',
            columnGap: '1.125rem',
            rowGap: '0.4rem',
            fontSize: '1rem',
          }}
        >
          {usage?.elapsedS != null && usage.elapsedS > 0 && row('Time taken', fmtDuration(usage.elapsedS))}
          {spend && row('Credits used', <strong>{spend}</strong>)}
          {totalTokens > 0 && row(
            'Work done',
            <>
              {usage?.calls ? `${usage.calls} AI call${usage.calls === 1 ? '' : 's'}, ` : ''}
              {fmtTokens(totalTokens)} tokens
              {(usage?.inputTokens || usage?.outputTokens) ? (
                <span style={{ color: 'var(--color-text-secondary)' }}>
                  {' '}({fmtTokens(usage?.inputTokens)} read · {fmtTokens(usage?.outputTokens)} written)
                </span>
              ) : null}
            </>,
          )}
          {files.length > 0 && row(
            'Files changed',
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {files.map((f) => (
                <li key={f} style={{ fontFamily: 'var(--font-mono)', fontSize: '0.875rem' }}>{f}</li>
              ))}
            </ul>,
          )}
        </dl>
      )}
      {stepsOpen && steps.length > 0 && (
        <div
          style={{
            padding: '0.75rem 0.875rem',
            border: '1px solid var(--color-border)',
            background: 'var(--color-surface-2)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          <StepList steps={steps} />
        </div>
      )}
    </div>
  )
}

/**
 * The pinned plan bar above the message box: what state the plan is in, "n of
 * m done", a progress bar, and View/Hide plan. The list itself (children) only
 * renders when open. Used for the build plan AND the CLI agents' checklist.
 */
export function PlanProgressCard({
  title, subtitle, progress, tone, open, onToggle, action, children,
}: {
  title: string
  subtitle?: string
  /** 0–100 */
  progress: number
  tone: 'running' | 'paused' | 'done' | 'error'
  open: boolean
  onToggle: () => void
  /** Optional compact action shown on the bar (e.g. Continue while paused). */
  action?: ReactNode
  children?: ReactNode
}) {
  const fill =
    tone === 'done' ? 'var(--color-success)'
      : tone === 'paused' ? 'var(--color-warning)'
      : tone === 'error' ? 'var(--color-error)'
      : 'linear-gradient(90deg, var(--gr-drkblue), var(--gr-drkgreen))'
  const pct = Math.max(0, Math.min(100, Math.round(progress)))
  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--color-surface-2)',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.625rem 0.875rem' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', fontSize: '1rem', minWidth: 0 }}>
            <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }} title={title}>
              {title}
            </span>
            {subtitle && (
              <span style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>{subtitle}</span>
            )}
          </div>
          <div
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={title}
            style={{ height: 6, background: 'var(--color-border)', borderRadius: 99, overflow: 'hidden' }}
          >
            <div style={{ width: `${pct}%`, height: '100%', background: fill, borderRadius: 99, transition: 'width 0.4s ease' }} />
          </div>
        </div>
        {action}
        {children && (
          <button type="button" onClick={onToggle} aria-expanded={open} style={{ ...linkBtn, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {open ? 'Hide plan' : 'View plan'}
          </button>
        )}
      </div>
      {open && children && (
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '0.625rem 0.875rem 0.75rem', maxHeight: 300, overflowY: 'auto' }}>
          {children}
        </div>
      )}
    </div>
  )
}

/** A warning or error the user must see — never folded into the steps. */
export function DockAlert({ level, children }: { level: 'warning' | 'error'; children: ReactNode }) {
  const isError = level === 'error'
  return (
    <div
      role={isError ? 'alert' : undefined}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.5rem',
        padding: '0.625rem 0.75rem',
        borderRadius: 'var(--radius-md)',
        background: isError ? 'var(--color-error-weak)' : 'var(--color-warning-weak)',
        border: `1px solid color-mix(in srgb, ${isError ? 'var(--color-error)' : 'var(--color-warning)'} 40%, transparent)`,
        fontSize: '1rem',
        lineHeight: 1.5,
        color: 'var(--color-text)',
      }}
    >
      {isError
        ? <XCircle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 3, color: 'var(--color-error)' }} />
        : <AlertTriangle size={16} aria-hidden="true" style={{ flexShrink: 0, marginTop: 3, color: 'var(--color-warning)' }} />}
      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</span>
    </div>
  )
}

/**
 * A build paused at a review boundary: ONE primary action (continue), the
 * "do the rest without stopping" option as secondary, and "one at a time" as a
 * plain link — the three used to be equal-weight buttons.
 */
export function ReviewCard({
  onContinue, onBuildRest, onOneByOne,
}: {
  onContinue: () => void
  onBuildRest?: () => void
  onOneByOne?: () => void
}) {
  return (
    <section
      aria-label="Ready for your review"
      style={{
        border: '1px solid color-mix(in srgb, var(--color-warning) 40%, transparent)',
        background: 'var(--color-warning-weak)',
        borderRadius: 'var(--radius-lg)',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <PauseCircle size={19} aria-hidden="true" style={{ color: 'var(--color-warning)', flexShrink: 0 }} />
        <span style={{ fontWeight: 600, fontSize: '1.0625rem' }}>Ready for your review</span>
      </div>
      <p style={{ margin: 0, fontSize: '1rem', lineHeight: 1.5, color: 'var(--color-text-secondary)' }}>
        Check what has been built so far. Ask for changes below, or carry on with the plan.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        <button type="button" className="btn btn-primary" onClick={onContinue} style={dockPrimaryBtn}
          title="Build the next phase, then pause again">
          Continue
        </button>
        {onBuildRest && (
          <button type="button" className="btn btn-secondary" onClick={onBuildRest} style={dockSecondaryBtn}
            title="Build all remaining objects without pausing">
            Build the rest
          </button>
        )}
      </div>
      {onOneByOne && (
        <button type="button" onClick={onOneByOne} style={{ ...linkBtn, alignSelf: 'flex-start', fontSize: '0.9375rem' }}
          title="Build just the next object, then pause again">
          Build one item at a time
        </button>
      )}
    </section>
  )
}
